import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import { SchedulablePresenter } from 'shared/presenters/SchedulablePresenter'
import { useRouter } from 'next/router'
import {
  LineItemInterface,
  StyledLineItem,
  StyledFlex,
  StyledDetailsFlex,
  StyledInfoFlex
} from './LineItem'
import Button from 'library/atoms/Button'
import { defined, definedNotNull } from 'shared/presenters/presenter'
import { Text, H6 } from 'library/atoms/Typography'
import Flex from 'shared/library/atoms/Flex'
import Thumbnail from 'shared/library/atoms/Thumbnail'
import Price from 'shared/library/atoms/Price'
import { determineChargeableReviewDataForSchedulables } from 'helpers/determineChargeableReviewData'
import {
  OptionalExtraPresenter,
  TimeSlotBookingPresenter
} from 'shared/presenters'
import dayjs from 'dayjs'
import formatRentalRequirements from 'helpers/formatRentalRequirements'
import formatOptionalExtras from 'shared/helpers/formatOptionalExtraData/formatOptionalExtras'
import useBasket from 'state/useBasket'
import InfoBar from 'library/molecules/InfoBar'
import Tag from 'library/atoms/Tag'
import Icon from 'shared/library/atoms/Icon'
import { BookingPresenter } from 'shared/presenters/BookingPresenter'
import styled from '@emotion/styled'
import { ResourceRequirementQuantities } from 'shared/types'
import { StyledExpandIcon } from 'library/molecules/DetailsList'

const LineItemBooking = ({
  outlet,
  item,
  removeItemOnClick,
  removeItemLoading,
  completeActivityVoucherBooking,
  setExpanded,
  expanded
}: LineItemInterface) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const { basket } = useBasket()
  const widgetSlug = router?.query.widgetSlug as string

  const booking = item?.booking && new BookingPresenter(item?.booking)

  const schedulable = new SchedulablePresenter(booking?.schedulable || {})

  const generateDataForChargeableReview = () => {
    const timeSlot =
      booking?.time_slot_booking &&
      new TimeSlotBookingPresenter(booking?.time_slot_booking)

    // Requirements
    const desiredResourceRequirements: ResourceRequirementQuantities = {}
    timeSlot?.time_slots?.[0]?.resource_assignment_placeholders?.forEach(
      resource => {
        const id = defined(resource.resource_requirement_id)
        // Look if key already exists and add this value to it
        const quantity = (desiredResourceRequirements?.[id] ?? 0) + 1
        desiredResourceRequirements[id] = quantity
      }
    )

    const rentalRequirements = formatRentalRequirements({
      desiredResourceRequirements,
      resourceRequirements: schedulable?.resource_requirements
    })

    // OptionalExtra
    const chosenOptionalExtras = defined(booking?.chosen_optional_extras)
    const optionalExtrasQuantities = booking?.getOptionalExtrasQuantities()

    const optionalExtraPresenter = chosenOptionalExtras?.map(
      item => new OptionalExtraPresenter(definedNotNull(item.optional_extra))
    )

    const optionalExtras = formatOptionalExtras(
      optionalExtraPresenter,
      optionalExtrasQuantities
    )

    return determineChargeableReviewDataForSchedulables({
      chargeable: schedulable,
      adults: defined(booking?.adultsGoing()),
      children: defined(booking?.childrenGoing()),
      timeSlot: {
        start: timeSlot && dayjs(timeSlot.start),
        finish: timeSlot && dayjs(timeSlot.finish)
      },
      rentalRequirements,
      optionalExtras,
      exclusive: defined(booking?.exclusive),
      total: defined(item.price_paid),
      basket: basket,
      t
    })
  }

  const chargeableReviewData = generateDataForChargeableReview()

  return (
    <>
      <StyledLineItem key={schedulable.id}>
        <Flex
          alignItems="center"
          onClick={() => {
            if (setExpanded) setExpanded(!expanded)
          }}
        >
          <Thumbnail src={schedulable.imageURL()} thumbnailSize={40} />
          <StyledFlex>
            <Text>{schedulable.name()}</Text>
            <Flex alignItems="center" gap={3}>
              {item?.price_paid && item?.price_paid > 0 ? (
                <Price
                  pence={defined(item.price_paid)}
                  currency={outlet.currency()}
                />
              ) : null}
              {item?.booking?.activity_voucher && (
                <StyledTag variant="icon">
                  <Icon name="voucher" color="gold" size={20} mr={3} />
                  <H6>{t('common:activity-voucher')}</H6>
                </StyledTag>
              )}
              <StyledExpandIcon expanded={!!expanded} />
            </Flex>
          </StyledFlex>
        </Flex>
        {expanded && (
          <>
            <StyledDetailsFlex>
              {chargeableReviewData?.lineItemSummary.when && (
                <StyledInfoFlex>
                  <Icon name="clock" size={20} mr={3} />
                  <Text color="secondary">
                    {chargeableReviewData.lineItemSummary.when}
                  </Text>
                </StyledInfoFlex>
              )}
              {chargeableReviewData?.lineItemSummary.participants && (
                <StyledInfoFlex>
                  <Icon name="profile" size={20} mr={3} />
                  <Text color="secondary">
                    {chargeableReviewData.lineItemSummary.participants}
                  </Text>
                </StyledInfoFlex>
              )}
              {chargeableReviewData?.lineItemSummary.rentals && (
                <StyledInfoFlex>
                  <Icon name="sup" size={20} mr={3} />
                  <Text color="secondary">
                    {chargeableReviewData.lineItemSummary.rentals}
                  </Text>
                </StyledInfoFlex>
              )}
              {chargeableReviewData?.lineItemSummary.addons && (
                <StyledInfoFlex>
                  <Icon name="add" size={20} mr={3} />
                  <Text color="secondary">
                    {chargeableReviewData.lineItemSummary.addons}
                  </Text>
                </StyledInfoFlex>
              )}
              {chargeableReviewData?.lineItemSummary.exclusive && (
                <StyledInfoFlex>
                  <Icon name="star-outline" size={20} mr={3} />
                  <Text color="secondary">{t('exclusive')}</Text>
                </StyledInfoFlex>
              )}
            </StyledDetailsFlex>
            {!item?.booking?.time_slot_booking && (
              <InfoBar variant="error" icon="info" size="small" mt={3}>
                {t('incomplete-booking')}
              </InfoBar>
            )}
            {(removeItemOnClick || chargeableReviewData) && expanded && (
              <>
                <Flex mt={2} justifyContent="end">
                  {removeItemOnClick && (
                    <Button
                      variant="tertiary"
                      loading={removeItemLoading}
                      inactive={removeItemLoading}
                      onClick={() => {
                        removeItemOnClick(definedNotNull(item.id))
                      }}
                      aria-label={t('remove-booking')}
                    >
                      {t('delete')}
                    </Button>
                  )}

                  {!item?.booking?.time_slot_booking &&
                    completeActivityVoucherBooking && (
                      <Button
                        variant="tertiary"
                        onClick={() =>
                          completeActivityVoucherBooking({
                            widgetSlug,
                            bookingId: booking?.id,
                            schedulableSlug: schedulable?.slug
                          })
                        }
                      >
                        {t('finish-booking')}
                      </Button>
                    )}
                </Flex>
              </>
            )}
          </>
        )}
      </StyledLineItem>
    </>
  )
}

export const StyledTag = styled(Tag)(() => ({
  padding: 0
}))

export default LineItemBooking
